import { useState } from "react";
import { PhoneIncoming, ShoppingBag, Clock } from "lucide-react";
import { reais, roi, dataBr } from "./api";

const canalCurto = (canal) => (canal === "voz" ? "Ligação" : "SMS");
const MAX_VIZINHAS = 3;

/** Um numero grande com rotulo embaixo — o mesmo desenho dos cards do funil. */
function Numero({ valor, rotulo, destaque }) {
  return (
    <div>
      <p className={`text-xl font-bold tabular-nums ${destaque ? "text-primary" : ""}`}>{valor}</p>
      <p className="text-xs text-muted-foreground">{rotulo}</p>
    </div>
  );
}

/**
 * "Quem recebeu e pediu": cruza o telefone de quem recebeu a campanha com o
 * telefone dos pedidos, na janela da campanha (backend: sms/cruzamento.py).
 *
 * Mostra dois recortes, decididos pelo dono: TODOS os pedidos de quem recebeu
 * e o que veio A MAIS do normal dessas mesmas pessoas. O primeiro inclui quem
 * ia pedir de qualquer jeito; o segundo e' o efeito da campanha.
 */
export default function VendasPorTelefone({ cz, ehVoz }) {
  const [aberta, setAberta] = useState(false);
  if (!cz) return null;

  const base = cz.base;
  const cresceu = cz.a_mais_pedidos !== null && cz.a_mais_receita_centavos > 0;
  const vizinhas = cz.tambem_receberam || [];

  return (
    <div className="rounded-xl border bg-card p-5 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <PhoneIncoming className="w-4 h-4 text-muted-foreground" /> Quem recebeu e pediu
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Telefone de quem recebeu cruzado com o telefone dos pedidos, até {cz.janela_dias} dias depois do envio.
            {ehVoz
              ? " A operadora não informa quem atendeu: conta todo mundo que recebeu a ligação."
              : " Também conta quem comprou pelo link, mesmo sem estar na lista (link repassado)."}
            {cz.pelo_link_pedidos > 0 &&
              ` Inclui ${cz.pelo_link_pedidos} ${cz.pelo_link_pedidos === 1 ? "pedido" : "pedidos"} pelo link.`}
          </p>
        </div>
        {cz.janela_aberta && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 text-amber-600 px-3 py-1 text-xs font-medium">
            <Clock className="w-3.5 h-3.5" /> Contando até {dataBr(cz.janela_fecha_em)}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Numero valor={cz.pedidos.toLocaleString("pt-BR")} rotulo={cz.pedidos === 1 ? "pedido" : "pedidos"} />
        <Numero valor={cz.clientes.toLocaleString("pt-BR")} rotulo={cz.clientes === 1 ? "cliente" : "clientes"} />
        <Numero valor={reais(cz.receita_centavos)} rotulo="faturado" />
        <Numero valor={roi(cz.roi_centesimos)} rotulo="retorno" destaque />
      </div>

      <div className="rounded-lg bg-muted/40 p-4">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">A mais que o normal</p>
        {base === null ? (
          <p className="text-sm text-muted-foreground">
            Ainda não dá pra comparar: a loja tinha menos de {cz.janela_dias} dias de pedidos antes do envio.
          </p>
        ) : cresceu ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Numero valor={`+${cz.a_mais_pedidos.toLocaleString("pt-BR")}`} rotulo="pedidos" />
            <div className="hidden sm:block" />
            <Numero valor={`+${reais(cz.a_mais_receita_centavos)}`} rotulo="faturado" />
            <Numero valor={roi(cz.roi_a_mais_centesimos)} rotulo="retorno" destaque />
          </div>
        ) : (
          <p className="text-sm">Essas pessoas pediram o mesmo ou menos que o normal delas.</p>
        )}
        {base !== null && (
          <p className="text-xs text-muted-foreground mt-2">
            Normal dessas pessoas: {base.pedidos.toLocaleString("pt-BR")} {base.pedidos === 1 ? "pedido" : "pedidos"} e {reais(base.receita_centavos)} a
            cada {cz.janela_dias} dias (média {base.semanas === 1 ? "do período" : `dos ${base.semanas} períodos`} antes do envio).
          </p>
        )}
      </div>

      {vizinhas.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Também receberam:{" "}
          {vizinhas.slice(0, MAX_VIZINHAS).map((v, i) => (
            <span key={v.id}>
              {i > 0 && " · "}
              <strong className="text-foreground font-medium">{canalCurto(v.canal)} #{v.seq} {v.nome}</strong>{" "}
              ({v.pedidos} {v.pedidos === 1 ? "pedido" : "pedidos"})
            </span>
          ))}
          {vizinhas.length > MAX_VIZINHAS && ` · e mais ${vizinhas.length - MAX_VIZINHAS} campanhas`}.
          {" "}Esses pedidos contam em cada campanha que a pessoa recebeu.
        </p>
      )}

      {cz.pedidos > 0 && (
        <button onClick={() => setAberta((v) => !v)}
          className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline">
          <ShoppingBag className="w-4 h-4" />
          {aberta ? "Ocultar os pedidos" : cz.pedidos === 1 ? "Ver o pedido" : `Ver os ${cz.pedidos} pedidos`}
        </button>
      )}
      {aberta && (
        <div className="divide-y -my-1">
          {cz.lista.map((p) => (
            <div key={p.pedido_id} className="py-2.5 flex items-center gap-3 text-sm">
              <span className="font-semibold tabular-nums shrink-0">{p.codigo}</span>
              {/* No celular a data desce pra baixo do nome: lado a lado o nome virava "M…". */}
              <div className="flex-1 min-w-0">
                <p className="truncate text-muted-foreground">{p.cliente_nome || "—"}</p>
                <p className="sm:hidden text-[11px] text-muted-foreground tabular-nums">{dataBr(p.criado_em)}</p>
              </div>
              {p.tambem.length > 0 && (
                <span className="hidden sm:inline text-[11px] text-muted-foreground shrink-0">
                  também {p.tambem.slice(0, MAX_VIZINHAS).map((s) => `#${s}`).join(" ")}
                  {p.tambem.length > MAX_VIZINHAS && " …"}
                </span>
              )}
              <span className="hidden sm:inline text-xs text-muted-foreground tabular-nums shrink-0">{dataBr(p.criado_em)}</span>
              <span className="font-semibold tabular-nums shrink-0">{reais(p.valor_centavos)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
